import { useContext, useState } from "react";
import { LayoutStateContext } from "../../../../contexts/layout_state";
import { useCachedValue } from "../../../../hooks/use_cached_value";
import { useCustomRouter } from "../../../../hooks/use_custom_router";
import { consumeReviewsHeader } from "../../../../hooks/use_reviews_header";
import { prebuildData } from "../../../../lib/prebuild-data";
import NicotineWarning from "../../../common/nicotine-warning";
import PriceDisplay from "../../../common/price-display";
import PriceDisplayWithDiscount from "../../../common/price-display-with-discount";
import { Image } from "../../../common/scaled-next-image";
import { EventPane } from "../../../core/event_pane";
import { useCssStateManager } from "../../../../hooks/use_css_state_manager";

export type CollectionsGridItemProps = {
    caption: string;
    price: number;
    discountedPrice: number;
    imageSrc: string;
    hoverImageSrc?: string;
    productId: string;
    teaseCaption?: string;
};

export function CollectionsGridItem({caption,price,discountedPrice,imageSrc,hoverImageSrc,productId,teaseCaption}:CollectionsGridItemProps)
{
    const cssStateManager = useCssStateManager(["collection-grid-item"]);
    cssStateManager.useProperty(!!teaseCaption,"has-tease");
    const layout = useContext(LayoutStateContext);
    const router = useCustomRouter();
    let { reviewsCount,starRating } = consumeReviewsHeader(productId,layout);
    const { mobileDetect: { widthCutoff: mobileCutoff } } = layout;

    // const reviewText = (reviewsCount!==-1)?((reviewsCount>0)?`${reviewsCount} customer review${(reviewsCount>1)?"s":""}`:"No customer reviews yet"):"---";
    const reviewText = (reviewsCount>0)?`(${reviewsCount})`:"No customer reviews";
    starRating = Math.min(Math.max(Math.round(starRating*2)/2,0),5);

    const stars = Array.from(Array(5)).map(() => {
        if (starRating>=1)
        {
            starRating--;
            return "star";
        }
        else if (starRating===0.5)
        {
            starRating = 0;
            return "star-half";
        }
        else
        {
            return "star-empty";
        }
    });

    const { priceDollars,priceCents } = (() => 
    {
        const priceStr = "00"+price;
        const cutOffIndex = priceStr.length-2;
        return { priceDollars: parseInt(priceStr.substring(0,cutOffIndex)).toString(),priceCents: priceStr.substring(cutOffIndex) };
    })();

    const [showImage,setShowImage] = useState(false);

    const productPageRoute = useCachedValue(() => prebuildData.getProductPageRoute(productId),productId);

    const clickHandler = () => {
        router.push(productPageRoute);
    };

    const { collectionItem: { priceColor,titleColor,priceHoverColor,titleHoverColor,reviewCountColor,reviewCountHoverColor }, general: { starsColor,starsHoverColor } } = prebuildData.getActiveTheme();

    return (
        <>
            <div className={cssStateManager.getClassNames()}>
                <div className={"image-square"+(showImage?" show":"")}>
                    <div className="inner">
                        <div className="inner-relative">
                            <Image src={imageSrc} layout="fill" onLoadingComplete={() => {
                                setShowImage(true);
                            }} />
                        </div>
                    </div>
                    {hoverImageSrc?(
                        <div className="overlay">
                            <div className="inner">
                                <Image src={hoverImageSrc} layout="fill" />
                            </div>
                        </div>
                    ):null}
                </div>
                <div className={"reviews"+((reviewsCount>0)?"":" none")}>
                    <span className="back">
                        <span className="stars">
                            {stars.map((type,index) => <i key={index} className={`icon-collection-item-review-${type}`} />)}
                        </span>
                        &nbsp;
                        <span className="reviews-text">{reviewText}</span>
                    </span>
                </div>                
                <div className="product-name">{caption}</div>
                <div className="tease-caption">{teaseCaption || ""}</div>
                <div className="price">                    
                    <PriceDisplayWithDiscount price={price} discountedPrice={discountedPrice} size={21} />
                </div>
                <div className="expand"></div>
                <NicotineWarning />
                <div className="bottom-margin"></div>
                <div className="mobile-overlay fill-parent"></div>
                <EventPane onClick={clickHandler} seoLinkUrl={productPageRoute} />
            </div>
            <style jsx>{`
            .collection-grid-item {
                width: 100%;
                position: relative;
                height: 100%;
                display: flex;
                flex-direction: column;
            }

            .expand {
                flex: 1 1 auto;
            }

            .image-square {
                width: 100%;
                padding: 50%;
                box-sizing: border-box;
                position: relative;
                --duration: 0.7s;
                visibility: hidden;
                opacity: 0;
            }

            .image-square.show {
                transition: visibility var(--duration),opacity var(--duration);
                visibility: visible;
                opacity: 1;
            }

            .image-square .inner {
                position: absolute;
                left: 0rem;
                top: 0rem;
                width: 100%;
                height: 100%;
            }

            .image-square .inner-relative {
                position: relative;
                width: 100%;
                height: 100%;
            }

            .overlay {
                position: absolute;
                left: 0rem;
                top: 0rem;
                width: 100%;
                height: 100%;
                opacity: 0;
            }

            .overlay .inner {
                position: relative;
                width: 100%;
                height: 100%;
            }

            .product-name,.price,.tease-caption {
                text-align: center;
                font-weight: 600;
                font-size: 20rem;
                margin-top: 8rem;                
                width: 100%;
                transition: color 0.2s;
            }

            .product-name {
                color: ${titleColor};
            }

            .price {
                color: ${priceColor};
            }

            .collection-grid-item.hover .product-name {
                color: ${titleHoverColor};
            }

            .collection-grid-item.hover .price {
                color: ${priceHoverColor};
            }

            .has-tease .price {
                display: none;
            }

            .tease-caption {
                display: none;
            }

            .has-tease .tease-caption {
                display: block;
                margin-top: 20rem;                
                font-weight: 400;
                font-size: 18rem;
            }

            .reviews {
                text-align: center;
                margin-top: 8rem;
            }

            .reviews .back {
                background-color: #FFF;
                padding: 4rem 10rem;
                padding-right: 13rem;
            }

            .stars {
                color: ${starsColor};
                letter-spacing: 3rem;
                font-size: 18rem;
                transition: color 0.2s;
            }

            .collection-grid-item.hover .stars {
                color: ${starsHoverColor};
            }            

            .reviews.none .stars {
                display: none;
            }

            .reviews-text {
                color: ${reviewCountColor};
                transition: color 0.2s;
            }

            .collection-grid-item.hover .reviews-text {
                color: ${reviewCountHoverColor};
            }            

            .reviews.none .reviews-text {
                color: ${titleColor};
            }

            .bottom-margin {
                height: 25rem;
                font-size: 13rem;
            }

            .collection-grid-item.hover .overlay {
                opacity: 1;
                transition: opacity 0.3s ease-in-out;
            }

            .sup,.sup-dollar {
                position: relative; top: -0.31em; left: 0.09em; font-size: 70%;
            }

            .sup-dollar {
                left: 0rem;
            }

            .between-stars-review-break {
                /* display: none; */
                display: inline;                
            }

            .mobile-overlay {
                position: absolute;
                background-color: rgba(0,0,0,0.045);
                opacity: 0;
                transition: opacity var(--duration);
            }

            @media screen and (max-width: ${mobileCutoff}px) {
                /*
                .collection-grid-item.touchdown .mobile-overlay {
                    opacity: 1;
                }
                */
            }

            `}</style>
        </>
    );
}